/**
 * Progress — Determinate progress bar for tracking completion of tasks.
 *
 * Progress Component Guidelines
 * - Use Progress to show determinate progress (uploads, multi-step flows, quotas).
 *   For indeterminate loading, use Spinner or Skeleton instead.
 * - Use the simple `<Progress>` for standalone bars where just the visual fill is
 *   needed. Switch to the composable API (`ProgressRoot` / `ProgressTrack` /
 *   `ProgressIndicator` / `ProgressLabel` / `ProgressValue`) when you need a label
 *   or numeric value displayed alongside the bar.
 * - Use `UNSAFE_indicatorClassName` to customize the indicator color for semantic
 *   meaning (e.g. green for healthy, red for over quota).
 * @see https://base-ui.com/react/components/progress Base UI Progress documentation
 * @see https://ui.shadcn.com/docs/components/progress shadcn/ui Progress documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { Progress as ProgressPrimitive } from "@base-ui/react/progress";
import { cn } from "@replohq/lattice/utils/cn";

interface ProgressRootProps<ClassName extends string = string>
  extends Omit<ProgressPrimitive.Root.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ProgressRoot<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ProgressRootProps<ClassName>) {
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn("w-full", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface ProgressTrackProps<ClassName extends string = string>
  extends Omit<ProgressPrimitive.Track.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ProgressTrack<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ProgressTrackProps<ClassName>) {
  return (
    <ProgressPrimitive.Track
      data-slot="progress-track"
      className={cn(
        "bg-surface relative h-1.5 w-full overflow-hidden rounded-full",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ProgressIndicatorProps<ClassName extends string = string>
  extends Omit<ProgressPrimitive.Indicator.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ProgressIndicator<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ProgressIndicatorProps<ClassName>) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn(
        "bg-default h-full rounded-full transition-all",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ProgressLabelProps<ClassName extends string = string>
  extends Omit<ProgressPrimitive.Label.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ProgressLabel<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ProgressLabelProps<ClassName>) {
  return (
    <ProgressPrimitive.Label
      data-slot="progress-label"
      className={cn("text-sm font-medium", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface ProgressValueProps<ClassName extends string = string>
  extends Omit<ProgressPrimitive.Value.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ProgressValue<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ProgressValueProps<ClassName>) {
  return (
    <ProgressPrimitive.Value
      data-slot="progress-value"
      className={cn(
        "text-muted ml-auto text-sm tabular-nums",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ProgressProps<ClassName extends string = string> {
  value: number;
  max?: number;
  min?: number;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  UNSAFE_indicatorClassName?: string;
}

function Progress<ClassName extends string>({
  value,
  max = 100,
  min = 0,
  className,
  UNSAFE_className,
  UNSAFE_indicatorClassName,
}: ProgressProps<ClassName>) {
  return (
    <ProgressRoot value={value} max={max} min={min}>
      <ProgressTrack className={className} UNSAFE_className={UNSAFE_className}>
        <ProgressIndicator UNSAFE_className={UNSAFE_indicatorClassName} />
      </ProgressTrack>
    </ProgressRoot>
  );
}

export {
  Progress,
  ProgressRoot,
  ProgressTrack,
  ProgressIndicator,
  ProgressLabel,
  ProgressValue,
};
